import type { CeoBankAccount } from '@shared/api';
import {
  ACCOUNT_TYPE_LABELS,
  formatDateTimeRu,
  formatMoney,
} from '../ceoFormat';
import { BankAccountHead, SplitRow, WrapButton } from '../ui';
import { payerGaps } from './innValidation';
import {
  AccNumber,
  AccountBox,
  AccountTypeLabel,
  Balance,
  BlockedAmountText,
  ConnectionError,
  MissingRequisites,
  OwnerLine,
  PurposeText,
  SyncRow,
} from './styles';

interface AccountCardProps {
  acc: CeoBankAccount;
  can_write: boolean;
  onOpenRequisites: (acc: CeoBankAccount) => void;
  onOpenBalance: (acc: CeoBankAccount) => void;
  onSync: (accId: string) => void;
}

export function AccountCard({
  acc,
  can_write,
  onOpenRequisites,
  onOpenBalance,
  onSync,
}: AccountCardProps) {
  const gaps = payerGaps(acc);
  const fromBank = Boolean(acc.connection_id);

  return (
    <AccountBox>
      <BankAccountHead account={acc} />
      <AccNumber>
        Р/с {acc.account_number} {acc.bik ? `· БИК ${acc.bik}` : ''}
      </AccNumber>
      <OwnerLine>
        {acc.entity_name || 'Владелец не указан'}
        {acc.entity_inn ? ` · ИНН ${acc.entity_inn}` : ''}
        {acc.entity_kpp ? ` · КПП ${acc.entity_kpp}` : ''}
      </OwnerLine>
      {gaps.length > 0 && (
        <MissingRequisites>
          <span>
            Нет: {gaps.join(', ')} — платёжки с этого счёта не выгрузятся в 1С.
          </span>
        </MissingRequisites>
      )}
      {can_write && (
        <WrapButton
          $size="sm"
          $variant="ghost"
          type="button"
          onClick={() => onOpenRequisites(acc)}
        >
          {gaps.length > 0 ? 'Дополнить реквизиты' : 'Реквизиты плательщика'}
        </WrapButton>
      )}
      <AccountTypeLabel>
        {ACCOUNT_TYPE_LABELS[acc.account_type] ?? acc.account_type}
      </AccountTypeLabel>
      <Balance>{formatMoney(acc.current_balance)}</Balance>
      <SplitRow>
        <span>Доступно:</span>
        <strong>{formatMoney(acc.available_balance)}</strong>
      </SplitRow>
      {acc.blocked_amount > 0 && (
        <SplitRow>
          <span>Заблокировано / ОБС резерв:</span>
          <BlockedAmountText>{formatMoney(acc.blocked_amount)}</BlockedAmountText>
        </SplitRow>
      )}
      {acc.interest_rate_pct > 0 && (
        <SplitRow>
          <span>Начисление на остаток:</span>
          <span>{acc.interest_rate_pct}% годовых</span>
        </SplitRow>
      )}
      <PurposeText>{acc.purpose_label}</PurposeText>
      {acc.api_error_message && <ConnectionError>{acc.api_error_message}</ConnectionError>}
      <SyncRow>
        <span>Обновлено: {formatDateTimeRu(acc.api_last_sync_at)}</span>
        {can_write && (
          <WrapButton
            $size="sm"
            $variant="ghost"
            onClick={() => (fromBank ? onSync(acc.id) : onOpenBalance(acc))}
          >
            {fromBank ? 'Обновить из банка' : 'Внести остаток'}
          </WrapButton>
        )}
      </SyncRow>
    </AccountBox>
  );
}
